import React, { useState, useEffect } from 'react';
import { questionsAPI, Question } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { QuestionMarkCircleIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const Questions: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadQuestions();
  }, [currentPage]);

  const loadQuestions = async () => {
    try {
      const response = await questionsAPI.getQuestions({
        page: currentPage,
        limit: 10
      });
      
      if (response.success && response.data) {
        setQuestions(response.data.items);
        setTotalPages(response.data.totalPages);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteQuestion = async (id: number) => {
    if (window.confirm('确定要删除这道题目吗？')) {
      try {
        await questionsAPI.deleteQuestion(id);
        loadQuestions();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const getQuestionTypeText = (type: string) => {
    const types: { [key: string]: string } = {
      single_choice: '单选题',
      multiple_choice: '多选题',
      true_false: '判断题',
      fill_blank: '填空题',
      subjective: '主观题'
    };
    return types[type] || type;
  };

  const getDifficultyText = (difficulty: number) => {
    const levels = ['', '简单', '较简单', '中等', '较难', '困难'];
    return levels[difficulty] || '未知';
  };

  if (loading) {
    return <LoadingSpinner className="py-12" />;
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            <QuestionMarkCircleIcon className="h-6 w-6 inline mr-2" />
            题库管理
          </h2>
          <button className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center">
            <PlusIcon className="h-4 w-4 mr-2" />
            添加题目
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {questions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">暂无题目</p>
          ) : (
            questions.map((question) => (
              <div key={question.id} className="border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {getQuestionTypeText(question.type)}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        难度: {getDifficultyText(question.difficulty)}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{question.title}</h3>
                    <p className="text-gray-700">{question.content}</p>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button className="p-2 text-gray-500 hover:text-blue-600">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => deleteQuestion(question.id)}
                      className="p-2 text-gray-500 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <nav className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <span className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default Questions;
